import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Dialog } from './Dialog'
import { FormLayout } from 'styles/FormLayout'
import { ColumnContainer, InnerContainer, Label } from 'styles/PageLayout'
import { MasterdataAssignment } from './masterdata-assignment/MasterdataAssignment'
import { useRemoteData } from 'lib/RemoteData'
import { API_HOST } from 'lib'
import { ScopeSummary } from './masterdata-assignment/ScopeSummary'
import {
  filterAppliedScopeFilters,
  fromInstallationScope,
  getInitialFilters,
  InstallationScopes,
  PathScope,
  ScopeFilterWithStatus,
  StructureNode,
  toInstallationScope,
  toScopeFilters,
  toScopeFiltersWithStatus,
} from 'lib/InstallationScope'
import { SelectedCountryContext } from 'domain/SelectedCountryContext'
import { SRD } from 'srd'
import { logError } from '../lib/logError'
import { LoadingSpinner } from './LoadingSpinner'
import { authenticatedGet } from '../lib/authenticatedGet'
import { FilterAssignment } from './masterdata-assignment/FilterAssignment'
import { CommonAssortmentWithCountForStatusTypes, ResponseObject, ScopeFilterDTO, ScopeFilters } from '../domain/DTO'

type Fields = {
  name: string
  installationScopes: InstallationScopes
  scopeFilters: ScopeFilters
}

type Props = {
  duplicatingFrom: CommonAssortmentWithCountForStatusTypes
  initialScopeFilters?: ScopeFilters
  onComplete: (fields: Fields) => Promise<void>
}

export const DuplicateCommonAssortmentDialog = ({ onComplete, duplicatingFrom, initialScopeFilters }: Props) => {
  const [showInstallationScopeConfiguration, setShowInstallationScopeConfiguration] = useState(false)
  const [showFilterOptionsConfiguration, setShowFilterOptionsConfiguration] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [name, setName] = useState('')
  const [modifiedScopes, setModifiedScopes] = useState<PathScope[]>([])
  const { selectedCountry } = useContext(SelectedCountryContext)
  const [modifiedScopeFilters, setModifiedScopeFilters] = useState<ScopeFilterWithStatus[]>([])

  useEffect(() => {
    authenticatedGet<ResponseObject<ScopeFilterDTO[]>>(
      `${API_HOST}/bff/commons/default-scope-filters?country=${selectedCountry}&commonId=${duplicatingFrom.commonAssortmentId}`,
    )
      .then((onfulfilled: { data: ScopeFilterDTO[] }) => {
        setModifiedScopeFilters(getInitialFilters(toScopeFiltersWithStatus(initialScopeFilters), onfulfilled.data))
      })
      .catch((err) => {
        setErrorMessage(err.toString())
      })
  }, [selectedCountry, initialScopeFilters])

  const transform = useCallback((original: { data: unknown }) => {
    const structureNodes = original.data as StructureNode[]
    const pathScopes = fromInstallationScope({ scopes: [] }, structureNodes)
    setModifiedScopes(pathScopes)
    return {
      structureNodes,
    }
  }, [])
  const { data: hierarchyTree } = useRemoteData(
    `${API_HOST}/v1/installation-hierarchy-tree?country=${selectedCountry}`,
    transform,
  )

  return (
    <div className="card">
      <FormLayout.Content>
        <h3>{`Duplicating from ${duplicatingFrom.name}`}</h3>
        <InnerContainer>
          <Label>
            <span>Name</span>
            <input type="text" className="textfield" value={name} onChange={(e) => setName(e.target.value)} />
          </Label>
        </InnerContainer>
        <ColumnContainer>
          {SRD.match(
            {
              notAsked: () => <div>Empty</div>,
              loading: () => (
                <React.Fragment>
                  <h3>Installation Scope</h3>
                  <LoadingSpinner />
                </React.Fragment>
              ),
              failure: (err) => <div className="alert alert-danger">Error: {err.body?.title}</div>,
              success: (value) => (
                <React.Fragment>
                  <div>
                    <h3>Installation Scope</h3>
                    <button
                      type="button"
                      className="btn btn-primary-dark py-sm"
                      onClick={() => setShowInstallationScopeConfiguration(true)}
                    >
                      CONFIGURE
                    </button>
                  </div>
                  <div>
                    <h3>Filter options</h3>
                    <button
                      type="button"
                      className="btn btn-primary-dark py-sm"
                      onClick={() => setShowFilterOptionsConfiguration(true)}
                    >
                      CONFIGURE filters
                    </button>
                  </div>
                  <ScopeSummary scopes={modifiedScopes} data={value.structureNodes} filters={modifiedScopeFilters} />
                  {showInstallationScopeConfiguration ? (
                    <Dialog
                      onClose={() => setShowInstallationScopeConfiguration(false)}
                      shouldCloseOnOverlayClick={false}
                    >
                      <div className="card overflow-y-auto max-h-[80vh]">
                        {' '}
                        <MasterdataAssignment
                          hierarchyData={value.structureNodes}
                          initialScopes={modifiedScopes}
                          onComplete={(scope) => {
                            setModifiedScopes(scope)
                            setShowInstallationScopeConfiguration(false)
                          }}
                        />
                      </div>
                    </Dialog>
                  ) : null}

                  {showFilterOptionsConfiguration ? (
                    <Dialog onClose={() => setShowFilterOptionsConfiguration(false)} shouldCloseOnOverlayClick={false}>
                      <div className="card overflow-y-auto max-h-[80vh]">
                        <FilterAssignment
                          modifiedScopeFilters={modifiedScopeFilters}
                          onComplete={(filters) => {
                            setErrorMessage(undefined)
                            setModifiedScopeFilters(filters)
                            setShowFilterOptionsConfiguration(false)
                          }}
                        />
                      </div>
                    </Dialog>
                  ) : null}
                </React.Fragment>
              ),
            },
            hierarchyTree,
          )}
        </ColumnContainer>
        <InnerContainer>{errorMessage ? <p>{errorMessage}</p> : null}</InnerContainer>
        <FormLayout.FooterContainer>
          <FormLayout.Footer>
            <button
              className="btn btn-primary-dark py-sm"
              disabled={!SRD.isSuccess(hierarchyTree) || !name || isLoading}
              onClick={() => {
                setIsLoading(true)
                onComplete({
                  name,
                  installationScopes: toInstallationScope(modifiedScopes),
                  scopeFilters: toScopeFilters(filterAppliedScopeFilters(modifiedScopeFilters)),
                }).catch((err) => {
                  logError(err)
                  setErrorMessage(err.toString())
                  setIsLoading(false)
                })
              }}
            >
              <FormLayout.LoadingButtonText loading={isLoading} text="Submit" loadingText="Submitting..." />
            </button>
          </FormLayout.Footer>
        </FormLayout.FooterContainer>
      </FormLayout.Content>
    </div>
  )
}
